const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/* =========================================================================
   El Laguito — enquiry pop-up.

   Serves two enquiry types from one modal:
   - Sold-out items (the default wording).
   - Price-upon-request items, i.e. products tagged 'price-on-request'. These are the
     Haute Creation / special-order pieces listed with a placeholder price of 0.00; the
     product page shows "Price upon request" instead of a money value and this enquiry
     button instead of add-to-cart. See snippets/price.liquid and snippets/buy-buttons.liquid.

   - Injects the pop-up inquiry form once per page.
   - Product pages: the Liquid snippet elc-availability-button renders the trigger
     (reliable SKU). A trigger may override the modal heading, intro, message placeholder
     and email subject via data-elc-title / -intro / -placeholder / -subject; anything not
     supplied falls back to DEFAULTS below, so one enquiry type never leaks into the next.
   - Collection / grid tiles: this script converts the disabled "Sold out" quick-add
     buttons into the same trigger, and labels the price-upon-request ones accordingly.
   - On submit it opens the customer's email pre-addressed to
     info@ellaguitocigars.ca with the product, SKU and their details filled in.
   - 2026-10-15: the email also names the exact variant the customer chose (e.g.
     "Colour: Blue & Brass"), with that variant's SKU and a ?variant= link. It is read at
     click time from the product section's variant-selects, which Dawn refreshes on every
     option change (the enquiry button itself is never re-rendered). See resolveVariant().
   - 2026-10-15: optional "Date required" field (the date the customer needs the piece by),
     sent as its own line in the email. Past dates cannot be picked.
   - 2026-10-30 (Zein: the enquiry email isn't formatted like the other emails): the enquiry is sent
     through the concierge mail Worker (elc-concierge-mail, kind "enquiry") — info@ gets the designed
     email (product card, variant, SKU, date required, phone as (416)-728-7455, Reply/Call, AI draft)
     and the customer gets a designed confirmation. The pop-up shows "Thank you" instead of opening the
     mail app. If the Worker can't be reached, the old mail-app email opens as before.
   ========================================================================= */
(function () {
  'use strict';
  var ELC_EMAIL = 'info@ellaguitocigars.ca';
  var MAIL_URL = 'https://elc-concierge-mail.ellaguitocigars.workers.dev';
  var MODAL_ID = 'elc-availability-modal';

  // Sold-out wording — the default for every trigger that does not override it.
  var DEFAULTS = {
    title: 'Ask about availability',
    intro: 'This item is currently sold out. Send us a note and we&rsquo;ll let you know when it&rsquo;s expected back in stock.',
    placeholder: 'e.g. When do you expect this back in stock?',
    subject: 'Availability inquiry: '
  };

  // Price-upon-request wording. Kept here so the product page and the collection tiles
  // stay in step; the product page passes the same strings through Liquid.
  var PRICE_ON_REQUEST = {
    label: 'Enquire — price upon request',
    title: 'Price upon request',
    intro: 'This is a limited edition piece, made to order. Send us a note and we will come back to you with current pricing and availability.',
    placeholder: 'e.g. Please send me the price and lead time for this piece.',
    subject: 'Price enquiry: '
  };

  function injectStyles() {
    if (document.getElementById('elc-availability-styles')) return;
    var css = ''
      + '.elc-availability-modal{display:none;position:fixed;inset:0;z-index:9999;align-items:center;justify-content:center;padding:2rem 1.5rem;}'
      + '.elc-availability-modal.is-open{display:flex;}'
      + '.elc-availability-modal__overlay{position:absolute;inset:0;background:rgba(0,0,0,.72);backdrop-filter:blur(2px);}'
      + '.elc-availability-modal__dialog{position:relative;width:100%;max-width:480px;max-height:90vh;overflow-y:auto;background:#0a0a0a;color:#f4ead8;border:1px solid rgba(201,168,76,.5);border-radius:2px;padding:2.4rem 2.4rem 2.6rem;box-shadow:0 24px 60px rgba(0,0,0,.5);font-family:var(--font-body-family,sans-serif);}'
      + '.elc-availability-modal__close{position:absolute;top:.8rem;right:1.1rem;background:none;border:none;color:#c9a84c;font-size:2.6rem;line-height:1;cursor:pointer;padding:.2rem;}'
      + '.elc-availability-modal__title{font-family:var(--font-heading-family,serif);color:#f4ead8;font-size:2.2rem;margin:0 0 .6rem;letter-spacing:.02em;}'
      + '.elc-availability-modal__intro{font-size:1.35rem;line-height:1.5;color:rgba(244,234,216,.8);margin:0 0 1.4rem;}'
      + '.elc-availability-modal__product{border-top:1px solid rgba(201,168,76,.35);border-bottom:1px solid rgba(201,168,76,.35);padding:1rem 0;margin-bottom:1.6rem;display:grid;gap:.5rem;}'
      + '.elc-availability-modal__product>div{display:flex;justify-content:space-between;gap:1.5rem;font-size:1.35rem;}'
      + '.elc-availability-modal__product span{text-transform:uppercase;letter-spacing:.14em;color:#c9a84c;font-size:1.1rem;}'
      + '.elc-availability-modal__product strong{text-align:right;font-weight:500;color:#f4ead8;}'
      + '.elc-availability-modal__form label{display:block;margin-bottom:1.15rem;}'
      + '.elc-availability-modal__form label>span{display:block;font-size:1.15rem;text-transform:uppercase;letter-spacing:.12em;color:rgba(244,234,216,.85);margin-bottom:.4rem;}'
      + '.elc-availability-modal__form label>span em{color:#c9a84c;font-style:normal;}'
      + '.elc-availability-modal__form input,.elc-availability-modal__form textarea{width:100%;background:#141414;border:1px solid rgba(201,168,76,.4);border-radius:2px;color:#f4ead8;padding:.85rem .95rem;font-size:1.4rem;font-family:inherit;box-sizing:border-box;}'
      + '.elc-availability-modal__form input:focus,.elc-availability-modal__form textarea:focus{outline:none;border-color:#c9a84c;box-shadow:0 0 0 2px rgba(201,168,76,.25);}'
      + '.elc-availability-modal__form textarea{resize:vertical;}'
      + '.elc-availability-modal__form input[type=date]{color-scheme:dark;min-height:2.6em;-webkit-appearance:none;appearance:none;text-align:left;}'
      + '.elc-availability-modal__form input[type=date]::-webkit-date-and-time-value{text-align:left;}'
      + '.elc-availability-modal__error{color:#e5a3a3;font-size:1.2rem;margin:0 0 1rem;}'
      + '.elc-availability-modal__submit{margin-top:.4rem;background:#c9a84c;color:#0a0a0a;border-color:#c9a84c;letter-spacing:.1em;}'
      + '.elc-availability-modal__submit:hover{opacity:.9;}'
      + '.elc-availability-modal__submit[disabled]{opacity:.6;cursor:wait;}'
      + '.elc-availability-modal__done{text-align:center;padding:.6rem 0 0;}'
      + '.elc-availability-modal__done p{font-size:1.45rem;line-height:1.6;color:#f4ead8;margin:0 0 1.6rem;}'
      + '.elc-availability-modal__done strong{color:#c9a84c;font-weight:500;}';
    var style = document.createElement('style');
    style.id = 'elc-availability-styles';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function buildModal() {
    if (document.getElementById(MODAL_ID)) return document.getElementById(MODAL_ID);
    injectStyles();
    var wrap = document.createElement('div');
    wrap.id = MODAL_ID;
    wrap.className = 'elc-availability-modal';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = ''
      + '<div class="elc-availability-modal__overlay" data-elc-close></div>'
      + '<div class="elc-availability-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="elc-availability-title">'
      + '<button type="button" class="elc-availability-modal__close" data-elc-close aria-label="Close">&times;</button>'
      + '<h2 id="elc-availability-title" class="elc-availability-modal__title">' + DEFAULTS.title + '</h2>'
      + '<p class="elc-availability-modal__intro">' + DEFAULTS.intro + '</p>'
      + '<div class="elc-availability-modal__product">'
      + '<div><span>Product</span><strong data-elc-product-name>&mdash;</strong></div>'
      + '<div data-elc-variant-row hidden><span>Variant</span><strong data-elc-variant>&mdash;</strong></div>'
      + '<div data-elc-sku-row><span>SKU</span><strong data-elc-sku>&mdash;</strong></div>'
      + '</div>'
      + '<form class="elc-availability-modal__form" data-elc-form novalidate>'
      + '<label><span>Name<em>*</em></span><input type="text" name="name" autocomplete="name" required></label>'
      + '<label><span>Email<em>*</em></span><input type="email" name="email" autocomplete="email" required></label>'
      + '<label><span>Contact number</span><input type="tel" name="phone" autocomplete="tel"></label>'
      + '<label><span>Date required</span><input type="date" name="date_required"></label>'
      + '<label><span>Message</span><textarea name="message" rows="4" placeholder="' + DEFAULTS.placeholder + '"></textarea></label>'
      + '<p class="elc-availability-modal__error" data-elc-error hidden>Please add your name and a valid email.</p>'
      + '<button type="submit" class="button button--full-width button--primary elc-availability-modal__submit">Send inquiry</button>'
      + '</form>'
      + '<div class="elc-availability-modal__done" data-elc-done hidden><p data-elc-done-text></p>'
      + '<button type="button" class="button button--full-width button--primary elc-availability-modal__submit" data-elc-close>Close</button></div>'
      + '</div>';
    document.body.appendChild(wrap);
    wireModal(wrap);
    return wrap;
  }

  var current = { product: '', variant: '', variantShort: '', sku: '', url: '', title: '', intro: '', placeholder: '', subject: '' };
  var lastFocused = null;

  function openModal(data) {
    var modal = buildModal();
    current = data;

    // Wording is reset from DEFAULTS on every open, so a price enquiry opened after an
    // availability enquiry (or the reverse) never inherits the previous copy.
    var titleEl = modal.querySelector('#elc-availability-title');
    var introEl = modal.querySelector('.elc-availability-modal__intro');
    var messageEl = modal.querySelector('textarea[name="message"]');
    if (titleEl) titleEl.textContent = data.title || DEFAULTS.title;
    if (introEl) {
      if (data.intro) { introEl.textContent = data.intro; }
      else { introEl.innerHTML = DEFAULTS.intro; }
    }
    if (messageEl) messageEl.setAttribute('placeholder', data.placeholder || DEFAULTS.placeholder);

    modal.querySelector('[data-elc-product-name]').textContent = data.product || '—';
    var variantRow = modal.querySelector('[data-elc-variant-row]');
    if (variantRow) {
      if (data.variant) {
        modal.querySelector('[data-elc-variant]').textContent = data.variant;
        variantRow.hidden = false;
      } else {
        variantRow.hidden = true;
      }
    }
    var skuRow = modal.querySelector('[data-elc-sku-row]');
    if (data.sku) {
      modal.querySelector('[data-elc-sku]').textContent = data.sku;
      skuRow.hidden = false;
    } else {
      skuRow.hidden = true;
    }
    modal.querySelector('[data-elc-error]').hidden = true;
    var formEl = modal.querySelector('[data-elc-form]'), doneEl = modal.querySelector('[data-elc-done]');
    if (formEl) formEl.hidden = false;
    if (doneEl) doneEl.hidden = true;
    var dateEl = modal.querySelector('input[name="date_required"]');
    if (dateEl) {
      var t = new Date();
      dateEl.setAttribute('min', t.getFullYear() + '-' + ('0' + (t.getMonth() + 1)).slice(-2) + '-' + ('0' + t.getDate()).slice(-2));
    }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var firstInput = modal.querySelector('input[name="name"]');
    if (firstInput) { setTimeout(function () { firstInput.focus(); }, 30); }
  }

  function closeModal() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && lastFocused.focus) { lastFocused.focus(); }
  }

  function wireModal(modal) {
    var form = modal.querySelector('[data-elc-form]');
    var errorEl = modal.querySelector('[data-elc-error]');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var data = new FormData(form);
      var name = (data.get('name') || '').toString().trim();
      var email = (data.get('email') || '').toString().trim();
      var phone = (data.get('phone') || '').toString().trim();
      var message = (data.get('message') || '').toString().trim();
      var dateRaw = (data.get('date_required') || '').toString().trim();
      var dateRequired = dateRaw;
      var dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateRaw);
      if (dm) {
        var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        dateRequired = MONTHS[parseInt(dm[2], 10) - 1] + ' ' + parseInt(dm[3], 10) + ', ' + dm[1];
      }
      var validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!name || !validEmail) { errorEl.hidden = false; return; }
      errorEl.hidden = true;
      var subjectPrefix = current.subject || DEFAULTS.subject;
      var kind = subjectPrefix.replace(/[:\s]+$/, '');
      var subject = subjectPrefix + current.product
        + (current.variantShort ? ' – ' + current.variantShort : '')
        + (current.sku ? ' (SKU ' + current.sku + ')' : '');
      var lines = [
        kind + ' from the El Laguito Cigars website',
        '',
        'Product: ' + current.product
      ];
      if (current.variant) { lines.push('Variant: ' + current.variant); }
      lines = lines.concat([
        'SKU: ' + (current.sku || 'N/A'),
        'Product page: ' + (current.url || 'N/A'),
        '',
        'Name: ' + name,
        'Email: ' + email,
        'Contact number: ' + (phone || 'Not provided'),
        'Date required: ' + (dateRequired || 'Not provided'),
        '',
        'Message:',
        (message || '(No message provided)')
      ]);
      var mailto = 'mailto:' + ELC_EMAIL
        + '?subject=' + encodeURIComponent(subject)
        + '&body=' + encodeURIComponent(lines.join('\n'));
      var openMailApp = function () { window.location.href = mailto; closeModal(); form.reset(); };
      if (!window.fetch) { openMailApp(); return; }
      // 2026-10-30: send through the mail Worker (designed emails to info@ and the customer).
      var btn = form.querySelector('button[type="submit"]');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      var ctl = window.AbortController ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctl) ctl.abort(); }, 30000);
      var restore = function () { clearTimeout(timer); if (btn) { btn.disabled = false; btn.textContent = label; } };
      fetch(MAIL_URL + '/send-copy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ctl ? ctl.signal : undefined,
        body: JSON.stringify({
          kind: 'enquiry', enquiry: kind, lang: /^fr/i.test(document.documentElement.lang || '') ? 'fr' : 'en',
          email: email, name: name, phone: phone, date_required: dateRequired, message: message,
          product: current.product, variant: current.variant, sku: current.sku, url: current.url
        })
      })
        .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { if (!r.ok || !d || !d.ok) throw new Error('send'); }); })
        .then(function () {
          restore();
          var modal = document.getElementById(MODAL_ID);
          var done = modal && modal.querySelector('[data-elc-done]');
          var first = name.split(/\s+/)[0];
          if (done) {
            done.querySelector('[data-elc-done-text]').innerHTML = 'Thank you, ' + escHtml(first) + '. Your enquiry has been sent to our concierge, and a confirmation is on its way to <strong>' + escHtml(email) + '</strong>. We will reply within one business day (Mon–Fri, 10 am–6 pm ET).';
            form.hidden = true; done.hidden = false;
          } else { closeModal(); }
          form.reset();
        })
        .catch(function () { restore(); openMailApp(); });
    });
  }

  function escHtml(t) {
    return String(t == null ? '' : t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  // ELC 2026-10-15 — which variant is the customer asking about?
  // Option names come from the trigger (data-option-names, set by elc-availability-button) or,
  // failing that, from the picker's own labels. The selected variant comes from the
  // variant-selects JSON in the same product section (product page or quick-add modal), which
  // Dawn replaces on every option change. Liquid's data-variant is the fallback for anything
  // rendered outside a product section. Single-variant products return no variant.
  function optionNames(trigger, root) {
    var names = [];
    var attr = trigger.getAttribute('data-option-names');
    if (attr) { try { names = JSON.parse(attr) || []; } catch (err) { names = []; } }
    if (!names.length && root) {
      var labels = root.querySelectorAll('variant-selects fieldset > legend, variant-selects .product-form__input--dropdown > label');
      for (var i = 0; i < labels.length; i++) {
        var t = '';
        for (var n = labels[i].firstChild; n; n = n.nextSibling) { if (n.nodeType === 3) t += n.textContent; }
        names.push(t.replace(/[:\s]+$/, '').trim());
      }
    }
    return names;
  }

  function resolveVariant(trigger) {
    var out = {
      variant: trigger.getAttribute('data-variant') || '',
      short: trigger.getAttribute('data-variant-values') || '',
      id: trigger.getAttribute('data-variant-id') || '',
      sku: ''
    };
    var root = trigger.closest('product-info') || trigger.closest('.quick-add-modal__content');
    var selects = root ? root.querySelector('variant-selects') : null;
    if (!selects) return out;
    var values = [];
    var v = null;
    var json = selects.querySelector('[data-selected-variant]');
    if (json) { try { v = JSON.parse(json.textContent); } catch (err) { v = null; } }
    if (v && v.options) {
      values = v.options;
      out.id = v.id ? String(v.id) : '';
      if (v.sku) out.sku = v.sku;
    } else {
      // A combination with no matching variant: report what the customer ticked.
      out.id = '';
      var picked = selects.querySelectorAll('fieldset input:checked, select');
      for (var i = 0; i < picked.length; i++) values.push(picked[i].value);
    }
    if (!values.length || (values.length === 1 && values[0] === 'Default Title')) {
      out.variant = '';
      out.short = '';
      return out;
    }
    var names = optionNames(trigger, root);
    var parts = [];
    var shortParts = [];
    for (var j = 0; j < values.length; j++) {
      if (values[j] == null || values[j] === '') continue;
      parts.push(names[j] ? names[j] + ': ' + values[j] : values[j]);
      shortParts.push(values[j]);
    }
    out.variant = parts.join(' / ');
    out.short = shortParts.join(' / ');
    return out;
  }

  // Delegated open/close so it also works for buttons added after load.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-elc-availability]');
    if (trigger) {
      e.preventDefault();
      lastFocused = trigger;
      var rv = resolveVariant(trigger);
      var product = trigger.getAttribute('data-product') || '';
      // Older markup may already have appended " — <variant>" to the product name.
      var dup = rv.short ? ' — ' + rv.short : '';
      if (dup && product.length > dup.length && product.slice(-dup.length) === dup) {
        product = product.slice(0, -dup.length);
      }
      var pageUrl = trigger.getAttribute('data-url') || '';
      if (rv.id) { pageUrl = pageUrl.split('?')[0] + '?variant=' + rv.id; }
      openModal({
        product: product,
        variant: rv.variant,
        variantShort: rv.short,
        sku: rv.sku || trigger.getAttribute('data-sku') || '',
        url: pageUrl,
        title: trigger.getAttribute('data-elc-title') || '',
        intro: trigger.getAttribute('data-elc-intro') || '',
        placeholder: trigger.getAttribute('data-elc-placeholder') || '',
        subject: trigger.getAttribute('data-elc-subject') || ''
      });
      return;
    }
    if (e.target.closest('[data-elc-close]')) { e.preventDefault(); closeModal(); }
  });

  document.addEventListener('keydown', function (e) {
    var modal = document.getElementById(MODAL_ID);
    if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) { closeModal(); }
  });

  // Convert sold-out quick-add buttons on grid / collection tiles into triggers.
  function scrapeSku(card) {
    var els = card.querySelectorAll('div, span, p');
    for (var i = 0; i < els.length; i++) {
      var txt = (els[i].textContent || '').trim();
      if (/^SKU:/i.test(txt) && els[i].children.length === 0) {
        return txt.replace(/^SKU:\s*/i, '').trim();
      }
    }
    return '';
  }

  function convertGridButtons() {
    var buttons = document.querySelectorAll('.quick-add__submit[disabled]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (btn.getAttribute('data-elc-done')) continue;
      var card = btn.closest('.card-wrapper') || btn.closest('.card');
      if (!card) continue;
      var titleLink = card.querySelector('.card__heading a') || card.querySelector('.card__heading');
      var title = titleLink ? titleLink.textContent.trim() : '';
      var linkEl = card.querySelector('a.full-unstyled-link[href], .card__heading a[href], a[href]');
      var url = linkEl ? linkEl.href : window.location.href;
      var sku = scrapeSku(card);
      // A price-upon-request tile is identified by the marker that snippets/price.liquid
      // renders in place of the money value. Those are not "sold out" — they are made to
      // order — so the tile gets the pricing wording rather than the restock wording.
      var isPriceOnRequest = !!card.querySelector('.elc-price-on-request');
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'elc-availability-trigger button button--full-width button--secondary';
      trigger.setAttribute('data-elc-availability', '');
      trigger.setAttribute('data-product', title);
      trigger.setAttribute('data-sku', sku);
      trigger.setAttribute('data-url', url);
      if (isPriceOnRequest) {
        trigger.setAttribute('data-elc-title', PRICE_ON_REQUEST.title);
        trigger.setAttribute('data-elc-intro', PRICE_ON_REQUEST.intro);
        trigger.setAttribute('data-elc-placeholder', PRICE_ON_REQUEST.placeholder);
        trigger.setAttribute('data-elc-subject', PRICE_ON_REQUEST.subject);
        trigger.textContent = PRICE_ON_REQUEST.label;
      } else {
        trigger.textContent = DEFAULTS.title;
      }
      btn.setAttribute('data-elc-done', '1');
      if (btn.parentNode) { btn.parentNode.replaceChild(trigger, btn); }
    }
  }

  function init() {
    convertGridButtons();
    // Re-run when Dawn re-renders sections (filtering, pagination, related products).
    if ('MutationObserver' in window) {
      var obs = new MutationObserver(function () { convertGridButtons(); });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
